import React, { useContext } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
    SimpleGrid,
    Box,
    Button,
    Collapse,
    FormControl,
    FormLabel,
    Input,
    InputGroup,
    InputRightElement,
    Radio,
    RadioGroup,
    Switch,
    Text,
    Select,
    useDisclosure
} from '@chakra-ui/react';
import { ArrowUpIcon, ArrowDownIcon, SearchIcon } from '@chakra-ui/icons';

import {
    animalGenders,
    animalTypes,
    animalByTypes
} from '../../../core/animal/animalConstants';
import { LandClass } from '../../../core/land';

import { ConfigContext, DataContext } from '../../../core/contexts';

export default function AnimalFilter(props: {
    animalFilter: Record<string, any>;
    setAnimalFilter: Function;
}): React.ReactElement {
    const { t } = useTranslation();
    const { lands } = useContext(DataContext);
    const { landId } = useParams();

    const { isOpen, onToggle } = useDisclosure();

    return (
        <Box>
            <FormControl mb='4'>
                <FormLabel mb='-2' fontSize={'sm'}>
                    {t('filters.filterById')}
                </FormLabel>
                <InputGroup>
                    <Input
                        type='text'
                        placeholder={t('filters.typeToSearch')}
                        variant='flushed'
                        value={
                            !!props.animalFilter.identifier
                                ? props.animalFilter.identifier
                                : ''
                        }
                        onChange={(e) =>
                            props.setAnimalFilter({
                                ...props.animalFilter,
                                identifier: e.target.value
                            })
                        }
                    />
                    <InputRightElement
                        pointerEvents='none'
                        children={<SearchIcon color='gray.300' />}
                    />
                </InputGroup>
            </FormControl>

            <FormControl isDisabled={!!landId} mb='4'>
                <FormLabel mb='-2' fontSize={'sm'}>
                    {t('filters.filterByLand')}
                </FormLabel>
                <Select
                    mt={'-2'}
                    name='land'
                    variant={'flushed'}
                    placeholder={t('filters.chooseOne', 'Filtrar por finca')}
                    value={props.animalFilter.land?.value || ''}
                    onChange={(e: any) => {
                        const land = e?.target?.value
                            ? {
                                  field: 'land',
                                  comparison: '==',
                                  value: e.target.value
                              }
                            : {};
                        props.setAnimalFilter({
                            ...props.animalFilter,
                            land
                        });
                    }}
                >
                    {lands?.map((land: LandClass) => (
                        <option key={land.id} value={land.id}>
                            {land.name}
                        </option>
                    ))}
                </Select>
            </FormControl>

            <FormControl mb='4'>
                <FormLabel mb='-2' fontSize={'sm'}>
                    {t('filters.filterBySpecie')}
                </FormLabel>
                <Select
                    name='specie'
                    variant={'flushed'}
                    placeholder={t('filters.chooseOne', 'Filtrar por Especie')}
                    value={props.animalFilter.specie?.value || ''}
                    onChange={(e: any) => {
                        const specie = e?.target?.value
                            ? {
                                  field: 'specie',
                                  comparison: '==',
                                  value: e.target.value
                              }
                            : {};
                        props.setAnimalFilter({
                            ...props.animalFilter,
                            specie
                        });
                    }}
                >
                    <ConfigContext.Consumer>
                        {(config) => (
                            <>
                                {config.animals.species.map((specie: any) => (
                                    <option key={specie} value={specie}>
                                        {t(`animal.species.${specie}`)}
                                    </option>
                                ))}
                            </>
                        )}
                    </ConfigContext.Consumer>
                </Select>
            </FormControl>

            <FormControl mb='4'>
                <FormLabel mb='0'>{t('filters.filterByGender')}</FormLabel>
                <RadioGroup
                    colorScheme={'green'}
                    value={props.animalFilter.gender?.value || ''}
                    onChange={(e: any) => {
                        const gender = e
                            ? {
                                  field: 'gender',
                                  comparison: '==',
                                  value: e
                              }
                            : {};
                        props.setAnimalFilter({
                            ...props.animalFilter,
                            gender
                        });
                    }}
                >
                    <SimpleGrid minChildWidth='30%'>
                        <Radio value={animalGenders.male}>
                            {t('animal.genders.male')}
                        </Radio>
                        <Radio value={animalGenders.female}>
                            {t('animal.genders.female')}
                        </Radio>
                        <Radio value=''>{t('filters.all')}</Radio>
                    </SimpleGrid>
                </RadioGroup>
            </FormControl>

            {props.animalFilter.specie?.value ? (
                <FormControl mb='4'>
                    <FormLabel mb='0'>{t('filters.filterByType')}</FormLabel>
                    <RadioGroup
                        colorScheme={'green'}
                        // value={props.animalFilter.type?.value || ''}
                        onChange={(e: any) => {
                            const type = e
                                ? {
                                      field: 'type',
                                      comparison: '==',
                                      value: e
                                  }
                                : {};
                            props.setAnimalFilter({
                                ...props.animalFilter,
                                type
                            });
                        }}
                    >
                        {animalByTypes.single.includes(
                            props.animalFilter.specie.value
                        ) && (
                            <SimpleGrid
                                minChildWidth='30%'
                                justifyContent={'space-between'}
                            >
                                <Radio value={animalTypes.adult}>
                                    {t('animal.types.adult')}
                                </Radio>
                                <Radio value={animalTypes.child}>
                                    {t('animal.types.child')}
                                </Radio>
                                <Radio value=''>{t('filters.all')}</Radio>
                            </SimpleGrid>
                        )}

                        {animalByTypes.group.includes(
                            props.animalFilter.specie.value
                        ) && (
                            <SimpleGrid
                                minChildWidth='30%'
                                justifyContent={'space-between'}
                            >
                                <Radio value={animalTypes.adult}>
                                    {t('animal.types.adult')}
                                </Radio>
                                <Radio value={animalTypes.group}>
                                    {t('animal.types.group')}
                                </Radio>
                                <Radio value=''>{t('filters.all')}</Radio>
                            </SimpleGrid>
                        )}
                    </RadioGroup>
                </FormControl>
            ) : (
                <></>
            )}

            {/*
            <Button
                rightIcon={isOpen ? <ArrowUpIcon /> : <ArrowDownIcon />}
                onClick={onToggle}
                variant='outline'
                w='full'
                mt='4'
                mb='-2'
            >
                {t('filters.advanced')}
            </Button>
            <Collapse in={isOpen} animateOpacity>
                <FormControl as='fieldset' mt='6'>
                    <FormLabel as='legend' m='0'>
                        Filtrar por estado
                    </FormLabel>
                    <RadioGroup colorScheme={'green'}>
                        <Radio size='sm' value='1' mr='2'>
                            Mantenimiento
                        </Radio>
                        <Radio size='sm' value='2' mr='2'>
                            Engorde
                        </Radio>
                        <Radio size='sm' value='3' mr='2'>
                            Embarazo
                        </Radio>
                        <Radio size='sm' value='4' mr='2'>
                            Con cria
                        </Radio>
                    </RadioGroup>
                </FormControl>

                <FormControl as='fieldset' mt='6'>
                    <FormLabel as='legend' m='0'>
                        Fecha nacimiento
                    </FormLabel>
                    <InputGroup w='45%' display='inline-block'>
                        <Text>Desde</Text>
                        <Input
                            size='sm'
                            type='date'
                            placeholder='fecha nacimiento'
                            onChange={(e: any) => {
                                const minBirthday = e
                                    ? {
                                          field: 'birthDate',
                                          comparison: '>',
                                          value: e.target.value
                                      }
                                    : {};
                                props.setAnimalFilter({
                                    ...props.animalFilter,
                                    minBirthday
                                });
                            }}
                        />
                    </InputGroup>
                    <InputGroup w='45%' display='inline-block' ml='4'>
                        <Text>Hasta</Text>
                        <Input
                            size='sm'
                            type='date'
                            placeholder='fecha nacimiento'
                            onChange={(e: any) => {
                                const maxBirthday = e
                                    ? {
                                          field: 'birthDate',
                                          comparison: '<',
                                          value: e.target.value
                                      }
                                    : {};
                                props.setAnimalFilter({
                                    ...props.animalFilter,
                                    maxBirthday
                                });
                            }}
                        />
                    </InputGroup>
                </FormControl>

                <FormControl as='fieldset' mt='6'>
                    <FormLabel as='legend' m='0'>
                        Fecha venta
                    </FormLabel>
                    <InputGroup w='45%' display='inline-block'>
                        <Text>Desde</Text>
                        <Input
                            size='sm'
                            type='date'
                            placeholder='fecha nacimiento'
                        />
                    </InputGroup>
                    <InputGroup w='45%' display='inline-block' ml='4'>
                        <Text>Hasta</Text>
                        <Input
                            size='sm'
                            type='date'
                            placeholder='fecha nacimiento'
                        />
                    </InputGroup>
                </FormControl>

                <FormControl as='fieldset' mt='6'>
                    <FormLabel as='legend' m='0'>
                        Vendido
                        <Switch
                            id='email-alerts'
                            colorScheme={'green'}
                            ml='4'
                        />
                    </FormLabel>
                </FormControl>
            </Collapse>
            */}
        </Box>
    );
}
